import "./Table.scss";

import React, { FunctionComponent } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { SvgIcon, Box, IconButton } from "@mui/material";
import { ICustomFooter, ICustomToolbar, TableProps } from "./ITable";
import CustomToolbar from "./components/CustomToolbar";
import CustomFooter from "./components/CustomFooter";
import { ReactComponent as Search } from "../../Assets/Images/search.svg";
import { ReactComponent as NoData } from "../../Assets/Images/NoData.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const Table: FunctionComponent<TableProps> = (props) => {
  const {
    rows,
    columns,
    density,
    loading,
    isCellEditable,
    columnVisibilityModel,
    disableColumnMenu,
    checkboxSelection,
    getRowId,
    rowCount,
    rowHeight,
    children,
    initialPageSize,
    pageSizeOptions,
    localeText,
    processRowUpdate,
    editMode,
    rowModesModel,
    onRowModesModelChange,
    onRowEditStop,
    sx = {
      boxShadow: "0px 0px 2px 0px rgba(145, 158, 171, 0.2)",
      borderRadius: "16px",
      "& .MuiDataGrid-row.Mui-hovered": { backgroundColor: "transparent" },
      "& .MuiDataGrid-row:hover": { backgroundColor: "transparent" },
    },
  } = props;

  // Classify children
  const customToolbar = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === CustomToolbar
  );

  const customFooter = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === CustomFooter
  );

  const customToolbarProps: ICustomToolbar | {} =
    React.isValidElement(customToolbar) && customToolbar.props
      ? (customToolbar.props as ICustomToolbar)
      : {};

  const customFooterProps: ICustomFooter | {} =
    React.isValidElement(customFooter) && customFooter.props
      ? (customFooter.props as ICustomFooter)
      : {};

  // Controlled pagination
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: initialPageSize ?? 20,
    page: 0,
  });

  const totalPages = Math.ceil(rows.length / paginationModel.pageSize);

  const handlePrevPage = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const handleNextPage = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, totalPages - 1),
    }));
  };

  // Custom footer with pagination arrows
  const FooterWithArrows: FunctionComponent = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={2}
      py={1}
      width="100%"
      sx={{ backgroundColor: "#fff", borderTop: "1px solid #e0e0e0" }}
    >
      {/* Left side: arrows */}
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={handlePrevPage}
          disabled={paginationModel.page === 0}
          size="small"
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleNextPage}
          disabled={paginationModel.page + 1 >= totalPages}
          size="small"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
        <span style={{fontSize:"!4px", fontWeight:"600"}}>
          Page {paginationModel.page + 1} of {totalPages}
        </span>
      </Box>

      {/* Right side: optional custom footer or info */}
      {customFooter && <CustomFooter {...customFooterProps} />}
    </Box>
  );

  return (
    <div style={{ height: 700, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode={editMode}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        onRowEditStop={onRowEditStop}
        localeText={{
          ...localeText,
          toolbarExport: "Export",
          toolbarExportCSV: "CSV",
          toolbarQuickFilterPlaceholder: "Search",
        }}
        sx={sx}
        isCellEditable={isCellEditable}
        className="Table"
        autoHeight={false} 
        density={density ?? "standard"}
        loading={loading}
        disableColumnMenu={disableColumnMenu ?? true}
        checkboxSelection={checkboxSelection}
        getRowId={getRowId}
        rowCount={rowCount}
        rowHeight={rowHeight}
        getRowHeight={() => "auto"}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={pageSizeOptions ?? [20, 50, 100]}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: customToolbar ? CustomToolbar : undefined,
          footer: FooterWithArrows,
          noRowsOverlay: CustomNoRows,
          quickFilterIcon: Search,
        }}
        slotProps={{
          toolbar: {
            ...customToolbarProps,
            GridToolbar: true,
            exportButton: true,
            exportCsv: { text: "CSV" },
          },
        }}
        initialState={{
          columns: { columnVisibilityModel },
        }}
      />
    </div>
  );
};

export default Table;

// ---------------- Custom No Rows Overlay ----------------
interface CustomNoRowsProps {}
const CustomNoRows: FunctionComponent<CustomNoRowsProps> = () => {
  return (
    <div className="custom_no_rows">
      <SvgIcon className="table_svg_icon" component={NoData} inheritViewBox />
      No Data Found
    </div>
  );
};
